import {
  getGridStringOperators,
  GridColDef,
  GridFilterInputValueProps,
} from '@components/DataTable';
import { extractYouTubeId, getChannelName, getYouTubeVideoId } from '@lib/utils/platforms';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip } from '@mui/material';
import { isProbablyImage } from '@pages/Library/utils/mediaStatus';
import { MediaTableRow } from '../cells/types';
import { COLUMN_DEFINITIONS, DEFAULT_COLUMN_ORDER } from './ColumnDefinitions';
import { ColumnFactoryProps } from './ColumnTypes';

const DefaultStringInput = getGridStringOperators().find((op) => op.InputComponent)?.InputComponent;

function YouTubeIdFilterInput(props: GridFilterInputValueProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%' }}>
      {DefaultStringInput && <DefaultStringInput {...props} />}
      <Tooltip title="You can enter space-separated YouTube IDs and/or URLs.">
        <InfoOutlinedIcon sx={{ fontSize: 16 * 1.6, opacity: 0.5, cursor: 'help' }} />
      </Tooltip>
    </div>
  );
}

const getRenderCellFunction = (field: string, renderFunctions: any) => {
  const renderMap: Record<string, any> = {
    filename: renderFunctions.renderTitleWithThumbnail,
    menu: renderFunctions.renderMenu,
    duration: renderFunctions.renderDuration,
    size: renderFunctions.renderSize,
    createdDate: renderFunctions.renderDateAdded,
    status: renderFunctions.renderStatus,
    frameCount: renderFunctions.renderFrameCount,
    width: renderFunctions.renderWidth,
    height: renderFunctions.renderHeight,
    channel: renderFunctions.renderChannel,
    createdAt: renderFunctions.renderDateAdded,
    publishedAt: renderFunctions.renderPublishedDate,
    youtubeId: renderFunctions.renderYouTubeId,
    isYouTubeShort: renderFunctions.renderIsYouTubeShort,
  };

  return renderMap[field];
};

const getValueGetterFunction = (field: string) => {
  const valueGetterMap: Record<string, (value: any, row: any) => any> = {
    filename: (value, row) => {
      const media = row as MediaTableRow;
      return media.metadata?.name || media.metadata?.filename || 'Unknown';
    },
    duration: (value, row) => {
      const media = row as MediaTableRow;
      // Return null (not 0) for images so DataTable's "is empty" / "= 0"
      // filters treat them as missing — matches the other sort/value getters
      // and the "—" sentinel rendered by DurationCell.
      return isProbablyImage(media) ? null : media.metadata?.video?.duration || 0;
    },
    size: (value, row) => {
      const media = row as MediaTableRow;
      return media.metadata?.video?.originalMediaSize || 0;
    },
    frameCount: (value, row) => {
      const media = row as MediaTableRow;
      return media.metadata?.video?.frameCount || 0;
    },
    width: (value, row) => {
      const media = row as MediaTableRow;
      return media.metadata?.video?.width || 0;
    },
    height: (value, row) => {
      const media = row as MediaTableRow;
      return media.metadata?.video?.height || 0;
    },
    channel: (value, row) => {
      const media = row as MediaTableRow;
      return getChannelName(media);
    },
    status: (value, row) => {
      const media = row as MediaTableRow;
      return media.status;
    },
    createdAt: (value, row) => {
      const media = row as MediaTableRow;
      return new Date(media.createdAt);
    },
    publishedAt: (value, row) => {
      const media = row as MediaTableRow;
      return media.publishedAt ? new Date(media.publishedAt) : null;
    },
    youtubeId: (value, row) => {
      const media = row as MediaTableRow;
      return getYouTubeVideoId(media) ?? '';
    },
    isYouTubeShort: (value, row) => {
      const media = row as MediaTableRow;
      return media.userMetadata?.isYoutubeShort === true;
    },
  };

  return valueGetterMap[field];
};

const getFilterOperators = (
  field: string,
  getGridNumericOperators: () => any[],
  DurationFilterInputValue: any,
  getGridSingleSelectOperators?: () => any[],
) => {
  const numericFields = ['duration', 'frameCount', 'width', 'height'];

  if (field === 'duration') {
    return getGridNumericOperators()
      .filter((operator) => operator.value !== 'isAnyOf')
      .map((operator) => ({
        ...operator,
        InputComponent: operator.InputComponent ? DurationFilterInputValue : undefined,
      }));
  }

  if (numericFields.includes(field)) {
    return getGridNumericOperators().filter((operator) => operator.value !== 'isAnyOf');
  }

  if (field === 'youtubeId') {
    return [
      {
        value: 'containsAny',
        label: 'matches any of',
        InputComponent: YouTubeIdFilterInput,
        getApplyFilterFn: (filterItem: { value?: string }) => {
          const raw = filterItem.value;
          if (!raw) return null;

          const tokens = raw.split(/\s+/).filter(Boolean);
          if (tokens.length === 0) return null;

          const exactIds: string[] = [];
          const substrings: string[] = [];

          for (const token of tokens) {
            const id = extractYouTubeId(token);
            if (id) {
              exactIds.push(id);
            } else {
              substrings.push(token);
            }
          }

          if (exactIds.length === 0 && substrings.length === 0) return () => false;

          const idSet = new Set(exactIds);
          return (cellValue: any) => {
            if (!cellValue) return false;
            if (idSet.has(cellValue)) return true;
            return substrings.some((sub) => cellValue.includes(sub));
          };
        },
      },
    ];
  }

  if (field === 'isYouTubeShort' && getGridSingleSelectOperators) {
    return getGridSingleSelectOperators().filter((operator) => operator.value === 'is');
  }

  return undefined;
};

export const createColumns = ({
  renderFunctions,
  videoStats,
  DurationFilterInputValue,
  getGridNumericOperators,
  getGridSingleSelectOperators,
}: ColumnFactoryProps): GridColDef[] => {
  const columns: GridColDef[] = DEFAULT_COLUMN_ORDER.map((field) => {
    const config = COLUMN_DEFINITIONS[field];
    if (!config) {
      throw new Error(`Column definition not found for field: ${field}`);
    }

    const baseColumn: GridColDef = {
      ...config,
      renderCell: getRenderCellFunction(field, renderFunctions),
      valueGetter: getValueGetterFunction(field),
      filterOperators: getFilterOperators(
        field,
        getGridNumericOperators,
        DurationFilterInputValue,
        getGridSingleSelectOperators,
      ),
    };

    // Add valueFormatter for singleSelect fields
    if (field === 'isYouTubeShort') {
      baseColumn.valueFormatter = (params: { value: boolean }) => {
        if (params.value === true) return 'Yes';
        if (params.value === false) return 'No';
        return 'Any';
      };
    }

    return baseColumn;
  });

  return columns;
};
